// Quality — data access (docs/design/quality.md, PR 1).
//
//   characteristics  table quality_characteristics (RLS writes with
//                    quality.plan.manage); a small tenant dictionary: one
//                    request (≤ 500 rows), code order (primary key).
//   plans            rpc inspection_plan_page(trigger, active, term, cursor…, limit 51):
//                    keyset (item_code, trigger, id) on inspection_plans_list_idx;
//                    inspection_plan_document(id): header + lines in one round-trip.
//   writes           save_inspection_plan(id, header, lines) with expected_revision.
// Numbers travel as decimal strings (Postgres numeric), never floats.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";
import { sortParam, type ListFilter, type ListSort } from "@patronix/domain/list";

export const characteristicKinds = ["measure", "attribute", "text"] as const;
export type CharacteristicKind = (typeof characteristicKinds)[number];
export const inspectionTriggers = ["purchase_receipt", "production_report", "manual"] as const;
export type InspectionTrigger = (typeof inspectionTriggers)[number];
export type ActiveFilter = "active" | "inactive" | "all";

export const isTrigger = (v: unknown): v is InspectionTrigger => (inspectionTriggers as readonly unknown[]).includes(v);
export const isActiveFilter = (v: unknown): v is ActiveFilter => v === "active" || v === "inactive" || v === "all";

export const CHARACTERISTIC_CODE = /^[A-Z0-9][A-Z0-9._-]{0,19}$/;
const CHARACTERISTIC_LIMIT = 500;
export const PLAN_PAGE = 50;
const STALE = 5 * 60_000;

export interface Characteristic {
  code: string;
  name: string;
  kind: CharacteristicKind;
  uom_id: string | null;
  decimals: number;
  is_active: boolean;
  uom: { code: string } | null;
}

export interface CharacteristicInput {
  name: string;
  uom_id: string | null;
  decimals: number;
  is_active: boolean;
}

export interface PlanRow {
  id: string;
  item_id: string;
  item_code: string;
  item_description: string;
  trigger: InspectionTrigger;
  supplier_code: string | null;
  supplier_name: string | null;
  sample_size: string | number | null;
  base_uom_code: string;
  line_count: number;
  is_active: boolean;
  updated_at: string;
}

export interface PlanLine {
  seq: number;
  characteristic_code: string;
  characteristic_name: string;
  kind: CharacteristicKind;
  uom_code: string | null;
  decimals: number;
  characteristic_active: boolean;
  target: string | number | null;
  lower_limit: string | number | null;
  upper_limit: string | number | null;
  required: boolean;
}

export interface PlanDocument {
  id: string;
  item_id: string;
  item_code: string;
  item_description: string;
  base_uom_code: string;
  trigger: InspectionTrigger;
  supplier_id: string | null;
  supplier_code: string | null;
  supplier_name: string | null;
  sample_size: string | number | null;
  note: string | null;
  is_active: boolean;
  revision: number;
  line_count: number;
  updated_at: string;
  updated_by_name: string | null;
  lines: PlanLine[];
}

export interface PlanListParams {
  trigger: InspectionTrigger | null;
  active: ActiveFilter;
  q: string;
  /** Column filters and sort (list RPC spec in @patronix/domain/list). */
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export interface PlanLineInput {
  seq: number;
  characteristic_code: string;
  target: string | null;
  lower_limit: string | null;
  upper_limit: string | null;
  required: boolean;
}

export interface PlanHeaderInput {
  item_id?: string;
  trigger?: InspectionTrigger;
  supplier_id?: string | null;
  sample_size: string | null;
  note: string | null;
  is_active: boolean;
  expected_revision?: number;
}

export const qualityKeys = {
  all: ["quality"] as const,
  characteristics: () => [...qualityKeys.all, "characteristics"] as const,
  plans: (p: PlanListParams) => [...qualityKeys.all, "plans", "list", p] as const,
  plan: (id: string) => [...qualityKeys.all, "plans", "detail", id] as const,
};

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}
const demoOff = () => new Error("Quality is not available in demo mode");

// ---------------------------------------------------------------------------
// Characteristics
// ---------------------------------------------------------------------------
async function listCharacteristics(signal: AbortSignal): Promise<Characteristic[]> {
  if (isDemoMode) return [];
  const { data, error } = await db()
    .from("quality_characteristics")
    .select("code,name,kind,uom_id,decimals,is_active,uom:uoms!quality_characteristics_uom_fkey(code)")
    .order("code")
    .limit(CHARACTERISTIC_LIMIT)
    .abortSignal(signal);
  if (error) throw error;
  return data as unknown as Characteristic[];
}

export const characteristicListQuery = queryOptions({
  queryKey: qualityKeys.characteristics(),
  queryFn: ({ signal }) => listCharacteristics(signal),
  staleTime: STALE,
});

export async function createCharacteristic(tenantId: string, code: string, kind: CharacteristicKind, input: CharacteristicInput) {
  if (isDemoMode) throw demoOff();
  const { error } = await db()
    .from("quality_characteristics")
    .insert({ tenant_id: tenantId, code, kind, ...input });
  if (error) throw error;
}

export async function updateCharacteristic(code: string, input: CharacteristicInput) {
  if (isDemoMode) throw demoOff();
  const { error } = await db().from("quality_characteristics").update(input).eq("code", code);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Inspection plans
// ---------------------------------------------------------------------------
/** Cursor "trigger|id|item_code" (the item code may contain "|", so it goes last). */
export async function listPlans(p: PlanListParams, cursor: string | null, signal: AbortSignal, size = PLAN_PAGE): Promise<Page<PlanRow>> {
  if (isDemoMode) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("inspection_plan_page", {
      p_trigger: p.trigger,
      p_active: p.active,
      p_term: p.q.trim() || null,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as (PlanRow & { sort_cursor: unknown })[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.sort_cursor) : null };
}

async function getPlan(id: string, signal: AbortSignal): Promise<PlanDocument | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("inspection_plan_document", { p_id: id }).abortSignal(signal);
  if (error) throw error;
  return (data as PlanDocument | null) ?? null;
}

export function planListQuery(p: PlanListParams) {
  return infiniteQueryOptions({
    queryKey: qualityKeys.plans(p),
    queryFn: ({ pageParam, signal }) => listPlans(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<PlanRow>) => last.nextCursor,
    staleTime: STALE,
  });
}

export function planDetailQuery(id: string) {
  return queryOptions({ queryKey: qualityKeys.plan(id), queryFn: ({ signal }) => getPlan(id, signal), staleTime: STALE });
}

export async function saveInspectionPlan(id: string | null, header: PlanHeaderInput, lines: PlanLineInput[]): Promise<{ id: string; revision: number }> {
  if (isDemoMode) throw demoOff();
  const { data, error } = await db().rpc("save_inspection_plan", { p_id: id, p_header: header, p_lines: lines });
  if (error) throw error;
  return data as { id: string; revision: number };
}

// ---------------------------------------------------------------------------
// Errors → translated messages
// ---------------------------------------------------------------------------
const lineRules: [RegExp, string][] = [
  [/sequence 1–999999 is required/, "quality.err.seq"],
  [/sequence \d+ appears twice/, "quality.err.seqTwice"],
  [/a characteristic is required/, "quality.err.characteristic"],
  [/^unknown characteristic/, "quality.err.characteristic"],
  [/is inactive$/, "quality.err.inactive"],
  [/appears twice$/, "quality.err.characteristicTwice"],
  [/limits are allowed only/, "quality.err.limitsMeasure"],
  [/lower limit is above/, "quality.err.limitsOrder"],
  [/target is outside/, "quality.err.target"],
];
const rules: [RegExp, string][] = [
  [/changed by someone else/, "quality.err.stale"],
  [/already exists/, "quality.err.duplicate"],
  [/supplier-specific/, "quality.err.supplierTrigger"],
  [/Supplier not found/, "quality.err.supplier"],
  [/service item/, "quality.err.service"],
  [/between 1 and 200/, "quality.err.lineCount"],
  [/sample size/, "quality.err.sample"],
  [/permission|row-level security|42501/i, "quality.err.permission"],
  [/duplicate key value/, "quality.err.codeTaken"],
];

export function qualityErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string, o?: Record<string, string>) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const line = /^Line (\d+): (.+)$/.exec(message);
  if (line) {
    const rule = lineRules.find(([re]) => re.test(line[2] ?? ""));
    return tr("quality.err.line", { line: line[1] ?? "", message: rule ? tr(rule[1]) : (line[2] ?? "") });
  }
  const rule = rules.find(([re]) => re.test(message));
  return rule ? tr(rule[1]) : message || tr("auth.errorGeneric");
}

/** Limits as text for lists and sheets: "599.5 – 600.5", "≤ 3", "≥ 1", or "—". */
export function limitsText(lower: string | number | null, upper: string | number | null, fmt: (v: string) => string): string {
  const lo = lower === null ? null : fmt(String(lower));
  const hi = upper === null ? null : fmt(String(upper));
  if (lo !== null && hi !== null) return `${lo} – ${hi}`;
  if (lo !== null) return `≥ ${lo}`;
  if (hi !== null) return `≤ ${hi}`;
  return "—";
}
